import React from 'react';
import { Box, BoxProps } from '../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../../CommonComponents/OPL/List/ListItem';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../CommonComponents/OPL/Menu/Menu';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../CommonComponents/OPL/Text/TruncatedText';
import { ColumnWidths, EntityDTO } from '../../DocumentVault.meta';
import { ENTITY_ICONS } from './Entity.meta';
import { ClickableEntityStyle } from './Entity.styles';
import { useIsDocumentVaultReadOnly } from '../../hooks/useIsDocumentVaultReadOnly';

interface Props extends BoxProps {
  entity: EntityDTO;
  onEntityClick(): void;
  onEntityDelete(): void;
  onOrganizeClick(): void;
  onRenameClick(): void;
}

export const Entity: React.FC<Props> = ({
  entity,
  onEntityClick,
  onEntityDelete,
  onOrganizeClick,
  onRenameClick,
  ...rest
}) => {
  const isDocumentVaultReadOnly = useIsDocumentVaultReadOnly();

  const canRename = entity.isRenameable && !isDocumentVaultReadOnly;

  const clickableEntityProps = {
    tabIndex: 0,
    role: 'button',
    ariaLabel: entity.name,
    onClick: onEntityClick,
    onKeyDown: evt => {
      if (evt.key === ' ' || evt.key === 'Enter') {
        onEntityClick();
      }
    },
  };

  return (
    <ClickableEntityStyle {...rest} {...clickableEntityProps}>
      <Box flex={ColumnWidths.Name} display="flex" alignItems="center">
        <Icon className={ENTITY_ICONS[entity.type]} color="neutral.500" mr="4" />

        <TruncatedText fontWeight={500}>{entity.name}</TruncatedText>
      </Box>

      <Box flex={ColumnWidths.LastModified}>
        {entity.lastModified && <Text>{entity.lastModified}</Text>}
      </Box>

      <Box flex={ColumnWidths.FileSize}>
        {entity.size && entity.size !== '0 B' && <Text>{entity.size}</Text>}
      </Box>

      <Box
        flex={ColumnWidths.Actions}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {!entity.isReadOnly && (
          <Box onClick={evt => evt.stopPropagation()}>
            <Menu>
              <MenuTrigger>
                <IconButton aria-label="Menu" icon="fas fa-ellipsis-h" variant="subtleInactive" />
              </MenuTrigger>

              <MenuContent>
                {canRename && (
                  <MenuItem onSelect={() => onRenameClick()}>
                    <ListItem icon="far fa-pen">Rename</ListItem>
                  </MenuItem>
                )}

                {!isDocumentVaultReadOnly && (
                  <MenuItem onSelect={() => onOrganizeClick()}>
                    <ListItem icon="far fa-folder-tree">Organize</ListItem>
                  </MenuItem>
                )}

                <MenuItem onSelect={() => onEntityDelete()}>
                  <ListItem icon="far fa-trash-alt">Delete</ListItem>
                </MenuItem>
              </MenuContent>
            </Menu>
          </Box>
        )}
      </Box>
    </ClickableEntityStyle>
  );
};
